/**
 * Team Management Page
 * Configure team leads, managers, approvers, and members for Change Management
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  People, 
  UserTick, 
  UserOctagon, 
  Crown,
  Edit2,
  Trash,
  Add,
  ArrowLeft,
  TickCircle,
  CloseCircle,
  Setting4
} from 'iconsax-react';
import toast from 'react-hot-toast';
import {
  getTeams,
  createTeam,
  updateTeam,
  Team,
  getChangeCategories,
  createChangeCategory,
  ChangeCategoryDef,
} from '../../services/changeManagementApi';
import { getUsers } from '../../services/api';
import { User } from '../../types';

const TeamManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [categories, setCategories] = useState<ChangeCategoryDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    department: '',
    team_lead: '',
    manager: '',
    members: [] as string[],
    approvers: [] as string[],
    management_approval_members: [] as string[],
    require_management_approval_for_high_risk: false
  });

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    code: '',
    description: '',
    department: '',
    team_lead: '',
    manager: '',
    members: [] as string[],
    approvers: [] as string[],
    management_approval_members: [] as string[],
    require_management_approval_for_high_risk: false,
    default_change_category: 'infrastructure' as string,
  });

  // Inline new category state (for Create Team modal)
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryCode, setNewCategoryCode] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsData, usersData, categoriesData] = await Promise.all([
        getTeams(true),
        getUsers({ status: 'active' }), // Only fetch active users
        getChangeCategories(false),
      ]);
      setTeams(teamsData);
      setCategories(categoriesData);
      // Filter out users with test/example emails and ensure they have valid names
      const filteredUsers = usersData.filter(user => {
        // Filter out test/example users
        const email = (user.email || '').toLowerCase();
        const isTestUser = email.includes('@example.com') || 
                          email.includes('test@') ||
                          email.includes('demo@') ||
                          email === 'admin@example.com' ||
                          email === 'partner@example.com' ||
                          email === 'org@example.com';
        
        // Only include users with valid names or usernames
        const hasValidName = (user.first_name && user.first_name.trim()) || 
                           (user.last_name && user.last_name.trim()) ||
                           (user.username && user.username.trim());
        
        return !isTestUser && hasValidName && user.status === 'active';
      });
      setUsers(filteredUsers);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTeam = (team: Team) => {
    setSelectedTeam(team);
    setEditForm({
      department: team.department || '',
      team_lead: team.team_lead || '',
      manager: team.manager || '',
      members: team.members || [],
      approvers: team.approvers || [],
      management_approval_members: team.management_approval_members || [],
      require_management_approval_for_high_risk: team.require_management_approval_for_high_risk || false
    });
    setIsEditModalOpen(true);
  };

  const handleOpenCreateTeam = () => {
    setCreateForm({
      name: '',
      code: '',
      description: '',
      department: '',
      team_lead: '',
      manager: '',
      members: [],
      approvers: [],
      management_approval_members: [],
      require_management_approval_for_high_risk: false,
      default_change_category: categories[0]?.code || 'infrastructure',
    });
    setIsAddingCategory(false);
    setNewCategoryName('');
    setNewCategoryCode('');
    setIsCreateModalOpen(true);
  };

  const handleSaveTeam = async () => {
    if (!selectedTeam) return;
    
    try {
      await updateTeam(selectedTeam.id, editForm);
      toast.success('Team updated successfully');
      setIsEditModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error('Failed to update team');
    }
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username : userId;
  };

  const toggleArrayItem = (array: string[], item: string, setter: (arr: string[]) => void) => {
    if (array.includes(item)) {
      setter(array.filter(i => i !== item));
    } else {
      setter([...array, item]);
    }
  };

  const handleCreateTeam = async () => {
    const name = createForm.name.trim();
    const code = createForm.code.trim();

    if (!name || !code) {
      toast.error('Please enter both Team Name and Code');
      return;
    }

    try {
      await createTeam({
        name,
        code,
        description: createForm.description.trim() || undefined,
        department: createForm.department.trim() || undefined,
        team_lead: createForm.team_lead || undefined,
        manager: createForm.manager || undefined,
        members: createForm.members,
        approvers: createForm.approvers,
        default_change_category: createForm.default_change_category || undefined,
        is_active: true,
      });
      toast.success('Team created successfully');
      setIsCreateModalOpen(false);
      await loadData();
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error('Failed to create team');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/change-management')}
            className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft size={20} color="#9CA3AF" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Setting4 size={28} color="#3B82F6" variant="Bulk" />
              Team Management
            </h1>
            <p className="text-gray-400 mt-1">
              Configure team leads, managers, and approvers for change management workflow
            </p>
          </div>
        </div>

        <button
          onClick={handleOpenCreateTeam}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        >
          <Add size={18} color="#FFFFFF" />
          <span>New Team</span>
        </button>
      </div>

      {/* Approval Workflow Info */}
      <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl p-6 border border-blue-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <UserTick size={24} color="#60A5FA" />
          Maker-Checker Approval Workflow
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                <span className="text-green-400 font-bold">1</span>
              </div>
              <span className="text-green-400 font-medium">Maker</span>
            </div>
            <p className="text-gray-400 text-sm">Any team member can create a change request (RFC)</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <span className="text-yellow-400 font-bold">2</span>
              </div>
              <span className="text-yellow-400 font-medium">Team Lead Review</span>
            </div>
            <p className="text-gray-400 text-sm">Team Lead reviews and approves/rejects the change</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                <span className="text-orange-400 font-bold">3</span>
              </div>
              <span className="text-orange-400 font-medium">Team Manager Approval</span>
            </div>
            <p className="text-gray-400 text-sm">Team Manager provides final approval for normal changes</p>
          </div>
          <div className="bg-gray-800/50 rounded-lg p-4 border border-orange-500/30">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                <span className="text-orange-400 font-bold">4</span>
              </div>
              <span className="text-orange-400 font-medium">Management Approval</span>
            </div>
            <p className="text-gray-400 text-sm">Management approval required for critical risk changes (when enabled)</p>
          </div>
        </div>
      </div>

      {/* Teams Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {teams.map(team => (
          <div 
            key={team.id} 
            className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden hover:border-blue-500/50 transition-colors"
          >
            {/* Team Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-700 p-4 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <People size={24} color="#60A5FA" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{team.name}</h3>
                    <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">{team.code}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleEditTeam(team)}
                  className="p-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 transition-colors"
                >
                  <Edit2 size={18} color="#60A5FA" />
                </button>
              </div>
              <p className="text-gray-400 text-sm mt-3">{team.description}</p>
            </div>

            {/* Team Details */}
            <div className="p-4 space-y-4">
              {/* Team Lead */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Crown size={18} color="#FBBF24" />
                  <span className="text-gray-400 text-sm">Team Lead</span>
                </div>
                <span className="text-white font-medium">
                  {team.team_lead ? getUserName(team.team_lead) : 
                    <span className="text-gray-500 italic">Not assigned</span>
                  }
                </span>
              </div>

              {/* Manager */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserOctagon size={18} color="#A78BFA" />
                  <span className="text-gray-400 text-sm">
                    {team.department
                      ? `${team.department} Manager`
                      : (team.name ? `${team.name} Manager` : 'Team Manager')}
                  </span>
                </div>
                <span className="text-white font-medium">
                  {team.manager ? getUserName(team.manager) : 
                    <span className="text-gray-500 italic">Not assigned</span>
                  }
                </span>
              </div>

              {/* Members Count */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <People size={18} color="#60A5FA" />
                  <span className="text-gray-400 text-sm">Members</span>
                </div>
                <span className="text-white font-medium">{team.members?.length || 0} members</span>
              </div>

              {/* Approvers Count */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserTick size={18} color="#34D399" />
                  <span className="text-gray-400 text-sm">Approvers</span>
                </div>
                <span className="text-white font-medium">{team.approvers?.length || 0} approvers</span>
              </div>

            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedTeam && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-800 p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Edit2 size={24} color="#60A5FA" />
                Configure {selectedTeam.name}
              </h2>
              <p className="text-gray-400 text-sm mt-1">Set up team roles and approval workflow</p>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Department / Function */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Department / Function
                </label>
                <input
                  type="text"
                  value={editForm.department}
                  onChange={(e) =>
                    setEditForm(prev => ({ ...prev, department: e.target.value }))
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Security, Integration, Engineering"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Used for manager labels (e.g. &quot;Security Manager&quot;)
                </p>
              </div>

              {/* Team Lead */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <Crown size={18} color="#FBBF24" />
                  Team Lead
                </label>
                <select
                  value={editForm.team_lead}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setEditForm(prev => ({ ...prev, team_lead: newValue }));
                  }}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Team Lead</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </option>
                  ))}
                </select>
                <p className="text-gray-500 text-xs mt-1">First level approver for change requests</p>
              </div>

              {/* Team Manager */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <UserOctagon size={18} color="#A78BFA" />
                  Team Manager
                </label>
                <select
                  value={editForm.manager}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setEditForm(prev => ({ ...prev, manager: newValue }));
                  }}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select R&D Manager</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </option>
                  ))}
                </select>
                <p className="text-gray-500 text-xs mt-1">Second level approver for change requests</p>
              </div>

              {/* Members */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <People size={18} color="#60A5FA" />
                  Team Members ({editForm.members.length} selected)
                </label>
                <div className="bg-gray-700 border border-gray-600 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {users.map(user => (
                    <label 
                      key={user.id} 
                      className="flex items-center gap-3 p-2 hover:bg-gray-600 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={editForm.members.includes(user.id)}
                        onChange={() => toggleArrayItem(
                          editForm.members, 
                          user.id, 
                          (arr) => setEditForm(prev => ({ ...prev, members: arr }))
                        )}
                        className="w-4 h-4 rounded border-gray-500 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-white">{user.first_name} {user.last_name}</span>
                      <span className="text-gray-400 text-sm">({user.email})</span>
                    </label>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-1">Members who can create change requests (Makers)</p>
              </div>

              {/* Approvers */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <UserTick size={18} color="#34D399" />
                  Approvers ({editForm.approvers.length} selected)
                </label>
                <div className="bg-gray-700 border border-gray-600 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {users.map(user => (
                    <label 
                      key={user.id} 
                      className="flex items-center gap-3 p-2 hover:bg-gray-600 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={editForm.approvers.includes(user.id)}
                        onChange={() => toggleArrayItem(
                          editForm.approvers, 
                          user.id, 
                          (arr) => setEditForm(prev => ({ ...prev, approvers: arr }))
                        )}
                        className="w-4 h-4 rounded border-gray-500 text-green-500 focus:ring-green-500"
                      />
                      <span className="text-white">{user.first_name} {user.last_name}</span>
                      <span className="text-gray-400 text-sm">({user.email})</span>
                    </label>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-1">Users who can approve change requests (Checkers)</p>
              </div>

              {/* Management Approval Members */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <Setting4 size={18} color="#F59E0B" />
                  Management Approval Members ({editForm.management_approval_members.length} selected)
                </label>
                <div className="bg-gray-700 border border-gray-600 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {users.map(user => (
                    <label 
                      key={user.id} 
                      className="flex items-center gap-3 p-2 hover:bg-gray-600 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={editForm.management_approval_members.includes(user.id)}
                        onChange={() => toggleArrayItem(
                          editForm.management_approval_members, 
                          user.id, 
                          (arr) => setEditForm(prev => ({ ...prev, management_approval_members: arr }))
                        )}
                        className="w-4 h-4 rounded border-gray-500 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-white">{user.first_name} {user.last_name}</span>
                      <span className="text-gray-400 text-sm">({user.email})</span>
                    </label>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-1">Members who provide management approval for high-risk changes</p>
              </div>

              {/* Require Management Approval for High-Risk Changes */}
              <div className="flex items-center gap-3 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <input
                  type="checkbox"
                  id="require_management_approval"
                  checked={editForm.require_management_approval_for_high_risk}
                  onChange={(e) => setEditForm(prev => ({ ...prev, require_management_approval_for_high_risk: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-500 text-orange-500 focus:ring-orange-500"
                />
                <label htmlFor="require_management_approval" className="text-gray-300 cursor-pointer">
                  <span className="font-medium">Require Management Approval for Critical Changes</span>
                  <p className="text-gray-500 text-xs mt-1">When enabled, critical risk changes will require approval from Management Approval Members</p>
                </label>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-800 p-6 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="px-6 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTeam}
                className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors flex items-center gap-2"
              >
                <TickCircle size={18} color="#3B82F6" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-800 p-6 border-b border-gray-700">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Add size={24} color="#60A5FA" />
                Create New Team
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Define a new team for change management workflow
              </p>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Team Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={createForm.name}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Integration Team"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Team Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={createForm.code}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 uppercase"
                    placeholder="e.g. INT"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                  rows={3}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Short description of what this team manages..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Department / Function
                </label>
                <input
                  type="text"
                  value={createForm.department}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, department: e.target.value }))
                  }
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. Security, Integration, Engineering"
                />
                <p className="text-gray-500 text-xs mt-1">
                  Used for manager labels (e.g. &quot;Security Manager&quot;)
                </p>
              </div>

              {/* Default Change Category */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Default Change Category
                </label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <select
                      value={createForm.default_change_category}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setCreateForm((prev) => ({
                          ...prev,
                          default_change_category: newValue,
                        }));
                      }}
                      className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.length === 0 && (
                        <option value="infrastructure">Infrastructure</option>
                      )}
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.code}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setIsAddingCategory((prev) => !prev)}
                      className="px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-sm text-gray-200 hover:bg-gray-600 transition-colors flex items-center gap-1"
                    >
                      <Add size={16} color="#E5E7EB" />
                      <span>{isAddingCategory ? 'Cancel' : 'Add Category'}</span>
                    </button>
                  </div>
                  <p className="text-gray-500 text-xs">
                    This category will be preselected when this team is chosen on the change form
                  </p>

                  {isAddingCategory && (
                    <div className="mt-3 space-y-3 rounded-lg border border-gray-700 bg-gray-900/60 p-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">
                            New Category Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder="e.g. Database"
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-300 mb-1">
                            New Category Code <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={newCategoryCode}
                            onChange={(e) =>
                              setNewCategoryCode(e.target.value.toLowerCase().replace(/\s+/g, '_'))
                            }
                            placeholder="e.g. database"
                            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingCategory(false);
                            setNewCategoryName('');
                            setNewCategoryCode('');
                          }}
                          className="px-3 py-1.5 rounded-lg bg-gray-800 text-xs text-gray-200 hover:bg-gray-700 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            const name = newCategoryName.trim();
                            const code = newCategoryCode.trim();
                            if (!name || !code) {
                              toast.error('Please enter category name and code');
                              return;
                            }
                            try {
                              const created = await createChangeCategory({
                                name,
                                code,
                                is_active: true,
                              });
                              toast.success('Category created');
                              // Update local list
                              setCategories((prev) => [...prev, created].sort((a, b) => {
                                return (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name);
                              }));
                              setCreateForm((prev) => ({
                                ...prev,
                                default_change_category: created.code,
                              }));
                              setIsAddingCategory(false);
                              setNewCategoryName('');
                              setNewCategoryCode('');
                            } catch (error) {
                              console.error('Error creating category:', error);
                              toast.error('Failed to create category');
                            }
                          }}
                          className="px-3 py-1.5 rounded-lg bg-blue-600 text-xs text-white hover:bg-blue-700 transition-colors flex items-center gap-1"
                        >
                          <TickCircle size={16} color="#3B82F6" />
                          <span>Save Category</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Team Lead */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <Crown size={18} color="#FBBF24" />
                  Team Lead
                </label>
                <select
                  value={createForm.team_lead}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setCreateForm((prev) => ({ ...prev, team_lead: newValue }));
                  }}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Team Lead</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </option>
                  ))}
                </select>
                <p className="text-gray-500 text-xs mt-1">
                  First level approver for change requests
                </p>
              </div>

              {/* Team Manager */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <UserOctagon size={18} color="#A78BFA" />
                  Team Manager
                </label>
                <select
                  value={createForm.manager}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setCreateForm((prev) => ({ ...prev, manager: newValue }));
                  }}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select R&D Manager</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.first_name} {user.last_name} ({user.email})
                    </option>
                  ))}
                </select>
                <p className="text-gray-500 text-xs mt-1">
                  Second level approver for change requests
                </p>
              </div>

              {/* Members */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <People size={18} color="#60A5FA" />
                  Team Members ({createForm.members.length} selected)
                </label>
                <div className="bg-gray-700 border border-gray-600 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {users.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-600 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={createForm.members.includes(user.id)}
                        onChange={() =>
                          toggleArrayItem(
                            createForm.members,
                            user.id,
                            (arr) =>
                              setCreateForm((prev) => ({
                                ...prev,
                                members: arr,
                              })),
                          )
                        }
                        className="w-4 h-4 rounded border-gray-500 text-blue-500 focus:ring-blue-500"
                      />
                      <span className="text-white">
                        {user.first_name} {user.last_name}
                      </span>
                      <span className="text-gray-400 text-sm">({user.email})</span>
                    </label>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Members who can create change requests (Makers)
                </p>
              </div>

              {/* Approvers */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <UserTick size={18} color="#34D399" />
                  Approvers ({createForm.approvers.length} selected)
                </label>
                <div className="bg-gray-700 border border-gray-600 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {users.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-600 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={createForm.approvers.includes(user.id)}
                        onChange={() =>
                          toggleArrayItem(
                            createForm.approvers,
                            user.id,
                            (arr) =>
                              setCreateForm((prev) => ({
                                ...prev,
                                approvers: arr,
                              })),
                          )
                        }
                        className="w-4 h-4 rounded border-gray-500 text-green-500 focus:ring-green-500"
                      />
                      <span className="text-white">
                        {user.first_name} {user.last_name}
                      </span>
                      <span className="text-gray-400 text-sm">({user.email})</span>
                    </label>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Users who can approve change requests (Checkers)
                </p>
              </div>

              {/* Management Approval Members */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <Setting4 size={18} color="#F59E0B" />
                  Management Approval Members ({createForm.management_approval_members.length} selected)
                </label>
                <div className="bg-gray-700 border border-gray-600 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {users.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 p-2 hover:bg-gray-600 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={createForm.management_approval_members.includes(user.id)}
                        onChange={() =>
                          toggleArrayItem(
                            createForm.management_approval_members,
                            user.id,
                            (arr) =>
                              setCreateForm((prev) => ({
                                ...prev,
                                management_approval_members: arr,
                              })),
                          )
                        }
                        className="w-4 h-4 rounded border-gray-500 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-white">
                        {user.first_name} {user.last_name}
                      </span>
                      <span className="text-gray-400 text-sm">({user.email})</span>
                    </label>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Members who provide management approval for high-risk changes
                </p>
              </div>

              {/* Require Management Approval for High-Risk Changes */}
              <div className="flex items-center gap-3 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
                <input
                  type="checkbox"
                  id="create_require_management_approval"
                  checked={createForm.require_management_approval_for_high_risk}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, require_management_approval_for_high_risk: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-500 text-orange-500 focus:ring-orange-500"
                />
                <label htmlFor="create_require_management_approval" className="text-gray-300 cursor-pointer">
                  <span className="font-medium">Require Management Approval for Critical Changes</span>
                  <p className="text-gray-500 text-xs mt-1">When enabled, critical risk changes will require approval from Management Approval Members</p>
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-800 p-6 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="px-6 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTeam}
                className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors flex items-center gap-2"
              >
                <TickCircle size={18} color="#3B82F6" />
                Create Team
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementPage;
